/**
 * iter 34 Phase E — Settings → Sidebar Preferences.
 *
 * Two stacked editors:
 *   1. **Your sidebar** — every user can reorder / hide nav items AND
 *      rename / reorder / remove section headers. Saving writes to
 *      /api/me/sidebar-preferences and broadcasts a window event so the
 *      live sidebar re-renders without a reload.
 *   2. **Org default** (admin-only) — same UI, writes to
 *      /api/org/sidebar-default. Affects every user who has not
 *      customised.
 *
 * The order array stores nav-item keys as-is and section headers as
 * `header:<text>` entries. Hidden flags are stored separately and only
 * apply to nav items. Header rows have an inline text input + delete
 * button (no hide toggle — drop them instead).
 */
import { useEffect, useState } from "react";
import { api, formatErr } from "../lib/api";
import { useAuth } from "../lib/auth";
import { NAV_CORE, NAV_CUSTOMER, NAV_CONTRACTOR, DEV_KEYS } from "./Sidebar";
import {
  CaretUp, CaretDown, Eye, EyeSlash, ArrowCounterClockwise, ListBullets,
  Plus, Trash,
} from "@phosphor-icons/react";

function navForOrgType(orgType) {
  const base =
    orgType === "customer" ? NAV_CUSTOMER
    : orgType === "contractor" ? NAV_CONTRACTOR
    : NAV_CORE;
  // Developer "safe zone" — never editable here.
  return base.filter(n => n.sec || !DEV_KEYS.has(n.key));
}

// Build the editable list of rows from prefs. Headers are stored as
// `header:<text>` keys; nav items keep their plain key. Items not in
// prefs.order are appended at the end with their default labels.
function buildEditableList(baseNav, prefs) {
  const items = baseNav.filter(n => !n.sec);
  const byKey = new Map(items.map(n => [n.key, { key: n.key, label: n.label }]));
  const order = (prefs?.order || []).filter(Boolean);
  const hidden = new Set((prefs?.hidden || []).filter(Boolean));
  const seen = new Set();
  const out = [];

  if (order.length === 0) {
    // No customisation yet — seed the editor with the live default
    // (sections + items in their hard-coded order) so the user starts
    // from the layout they actually see.
    for (const n of baseNav) {
      if (n.sec) {
        out.push({ kind: "header", text: n.sec });
      } else {
        out.push({ kind: "item", key: n.key, label: n.label, hidden: hidden.has(n.key) });
        seen.add(n.key);
      }
    }
    return out;
  }

  for (const k of order) {
    if (k.startsWith("header:")) {
      out.push({ kind: "header", text: k.slice(7) });
      continue;
    }
    const m = byKey.get(k);
    if (m && !seen.has(k)) {
      out.push({ kind: "item", key: m.key, label: m.label, hidden: hidden.has(k) });
      seen.add(k);
    }
  }
  for (const it of items) {
    if (!seen.has(it.key)) {
      out.push({ kind: "item", key: it.key, label: it.label, hidden: hidden.has(it.key) });
    }
  }
  return out;
}


export default function SidebarPreferencesSection() {
  const { user } = useAuth();
  return (
    <div className="surface p-6 mb-6" data-testid="sidebar-prefs-section">
      <div className="flex items-center gap-2 mb-1">
        <ListBullets size={18} />
        <h2 className="text-lg font-medium">Sidebar Layout</h2>
      </div>
      <p className="text-xs text-muted-foreground mb-4">
        Reorder, hide, or rename items in the left sidebar. Section headers are editable
        text rows that you can drop, rename, or move alongside the links. Your override
        wins over the org default — saving applies immediately, no reload needed.
      </p>

      <SidebarEditor
        scope="user"
        title="Your sidebar"
        endpointGet="/me/sidebar-preferences"
        endpointPut="/me/sidebar-preferences"
        endpointDelete="/me/sidebar-preferences"
        canEdit
        orgType={user?.org_type}
      />

      {user?.role === "admin" && (
        <div className="mt-6 pt-6 border-t border-border">
          <SidebarEditor
            scope="org"
            title="Org default (applies to users who have not customised)"
            endpointGet="/org/sidebar-default"
            endpointPut="/org/sidebar-default"
            canEdit
            orgType={user?.org_type}
          />
        </div>
      )}
    </div>
  );
}


function SidebarEditor({ scope, title, endpointGet, endpointPut, endpointDelete, canEdit, orgType }) {
  const baseNav = navForOrgType(orgType);
  const [list, setList] = useState([]);
  const [prefs, setPrefs] = useState({ order: [], hidden: [] });
  const [showTeamLive, setShowTeamLive] = useState(true);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState("");
  const [msg, setMsg] = useState("");

  const load = async () => {
    setLoading(true); setErr(""); setMsg("");
    try {
      const r = await api.get(endpointGet);
      const p = r.data || { order: [], hidden: [] };
      setPrefs(p);
      setShowTeamLive(p.show_team_live !== false);   // default true when absent
      setList(buildEditableList(baseNav, p));
    } catch (e) { setErr(formatErr(e)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [endpointGet, orgType]);

  const moveUp = (i) => {
    if (i <= 0) return;
    setList(l => {
      const n = l.slice();
      [n[i - 1], n[i]] = [n[i], n[i - 1]];
      return n;
    });
  };
  const moveDown = (i) => {
    setList(l => {
      if (i >= l.length - 1) return l;
      const n = l.slice();
      [n[i + 1], n[i]] = [n[i], n[i + 1]];
      return n;
    });
  };
  const toggleHide = (i) => {
    setList(l => l.map((it, idx) => idx === i ? { ...it, hidden: !it.hidden } : it));
  };
  const updateHeaderText = (i, text) => {
    setList(l => l.map((it, idx) => idx === i ? { ...it, text } : it));
  };
  const deleteRow = (i) => {
    setList(l => l.filter((_, idx) => idx !== i));
  };
  const addHeader = () => {
    setList(l => [...l, { kind: "header", text: "NEW SECTION" }]);
  };

  const save = async () => {
    setSaving(true); setErr(""); setMsg("");
    try {
      const body = {
        order: list.map(it =>
          it.kind === "header" ? `header:${(it.text || "").trim()}` : it.key
        ),
        hidden: list.filter(it => it.kind === "item" && it.hidden).map(it => it.key),
        show_team_live: showTeamLive,
      };
      await api.put(endpointPut, body);
      setMsg("Saved.");
      window.dispatchEvent(new CustomEvent("sidebar-prefs-changed"));
      // refresh local prefs so the dirty diff resets
      setPrefs({ order: body.order, hidden: body.hidden });
    } catch (e) { setErr(formatErr(e)); }
    finally { setSaving(false); }
  };

  const reset = async () => {
    if (!confirm(scope === "org"
      ? "Reset the org default to the built-in layout?"
      : "Reset YOUR sidebar to the org default?")) return;
    setSaving(true); setErr(""); setMsg("");
    try {
      if (endpointDelete) {
        await api.delete(endpointDelete);
      } else {
        await api.put(endpointPut, { order: [], hidden: [] });
      }
      setMsg("Reset.");
      window.dispatchEvent(new CustomEvent("sidebar-prefs-changed"));
      await load();
    } catch (e) { setErr(formatErr(e)); }
    finally { setSaving(false); }
  };

  const dirty = (() => {
    const a = list.map(it => it.kind === "header" ? `header:${(it.text || "").trim()}` : it.key).join("|");
    const b = (prefs.order || []).join("|");
    const ah = list.filter(it => it.kind === "item" && it.hidden).map(it => it.key).sort().join(",");
    const bh = (prefs.hidden || []).slice().sort().join(",");
    // iter 73.13 — Team Live toggle dirtiness (user scope only).
    const teamLiveDirty = scope === "user"
      && (showTeamLive !== (prefs.show_team_live !== false));
    return a !== b || ah !== bh || teamLiveDirty;
  })();

  return (
    <div data-testid={`sidebar-editor-${scope}`}>
      <div className="flex items-center justify-between mb-3">
        <div className="text-sm font-medium">{title}</div>
        <div className="flex gap-2">
          <button
            type="button"
            className="btn btn-ghost btn-sm"
            onClick={addHeader}
            disabled={saving || loading || !canEdit}
            data-testid={`sidebar-${scope}-add-header`}
            title="Add a section header"
          >
            <Plus size={12} /> ADD HEADER
          </button>
          <button
            type="button"
            className="btn btn-ghost btn-sm"
            onClick={reset}
            disabled={saving || loading}
            data-testid={`sidebar-${scope}-reset`}
            title="Reset to default"
          >
            <ArrowCounterClockwise size={12} /> RESET
          </button>
          <button
            type="button"
            className="btn btn-primary btn-sm"
            onClick={save}
            disabled={saving || loading || !dirty || !canEdit}
            data-testid={`sidebar-${scope}-save`}
          >
            {saving ? "SAVING…" : "SAVE"}
          </button>
        </div>
      </div>

      {err && <div className="text-xs text-destructive mb-2" data-testid={`sidebar-${scope}-error`}>{err}</div>}
      {msg && <div className="text-xs text-green-600 mb-2" data-testid={`sidebar-${scope}-msg`}>{msg}</div>}

      {/* iter 73.13 — Team Live widget visibility toggle (user scope only).
          Kept above the layout editor because it controls a pinned section
          that renders above every nav item, so its position mirrors the UI. */}
      {scope === "user" && !loading && (
        <label
          className="flex items-start gap-2 mb-3 p-2.5 rounded border border-border bg-muted/10 cursor-pointer hover:bg-muted/20 max-w-md"
          data-testid="sidebar-user-team-live-toggle-label"
        >
          <input
            type="checkbox"
            className="mt-0.5"
            checked={showTeamLive}
            onChange={(e) => setShowTeamLive(e.target.checked)}
            data-testid="sidebar-user-team-live-toggle"
            disabled={saving}
          />
          <div className="text-xs leading-snug">
            <div className="font-medium">Show &quot;Team Live&quot; at top of sidebar</div>
            <div className="text-muted-foreground text-[11px] mt-0.5">
              Live status dots for your teammates (green Active · grey Offline · amber On Break · red Busy).
              Uncheck to hide the widget entirely.
            </div>
          </div>
        </label>
      )}

      {loading ? (
        <div className="text-xs text-muted-foreground">Loading…</div>
      ) : (
        <div className="space-y-1 max-w-md" data-testid={`sidebar-${scope}-list`}>
          {list.map((it, i) =>
            it.kind === "header" ? (
              <div
                key={`h-${i}`}
                className="flex items-center gap-2 p-2 border border-dashed border-border rounded bg-muted/20"
                data-testid={`sidebar-${scope}-row-header-${i}`}
              >
                <span className="text-[10px] mono text-muted-foreground w-6">{i + 1}</span>
                <input
                  className="input flex-1 mono text-[11px] uppercase"
                  value={it.text || ""}
                  placeholder="SECTION HEADER"
                  onChange={(e) => updateHeaderText(i, e.target.value)}
                  data-testid={`sidebar-${scope}-header-input-${i}`}
                />
                <span className="text-[10px] text-muted-foreground mono">HEADER</span>
                <button
                  type="button"
                  className="btn btn-ghost btn-sm"
                  onClick={() => deleteRow(i)}
                  data-testid={`sidebar-${scope}-delete-header-${i}`}
                  title="Delete this header"
                >
                  <Trash size={12} />
                </button>
                <button
                  type="button"
                  className="btn btn-ghost btn-sm"
                  onClick={() => moveUp(i)}
                  disabled={i === 0}
                  data-testid={`sidebar-${scope}-up-header-${i}`}
                  title="Move up"
                >
                  <CaretUp size={12} />
                </button>
                <button
                  type="button"
                  className="btn btn-ghost btn-sm"
                  onClick={() => moveDown(i)}
                  disabled={i === list.length - 1}
                  data-testid={`sidebar-${scope}-down-header-${i}`}
                  title="Move down"
                >
                  <CaretDown size={12} />
                </button>
              </div>
            ) : (
              <div
                key={`it-${it.key}`}
                className={`flex items-center gap-2 p-2 border border-border rounded ${it.hidden ? "opacity-60" : ""}`}
                data-testid={`sidebar-${scope}-row-${it.key}`}
              >
                <span className="text-[10px] mono text-muted-foreground w-6">{i + 1}</span>
                <span className="flex-1 text-sm">{it.label}</span>
                {it.hidden && <span className="text-[10px] text-amber-600 mono">HIDDEN</span>}
                <button
                  type="button"
                  className="btn btn-ghost btn-sm"
                  onClick={() => toggleHide(i)}
                  data-testid={`sidebar-${scope}-hide-${it.key}`}
                  title={it.hidden ? "Show" : "Hide"}
                >
                  {it.hidden ? <EyeSlash size={12} /> : <Eye size={12} />}
                </button>
                <button
                  type="button"
                  className="btn btn-ghost btn-sm"
                  onClick={() => moveUp(i)}
                  disabled={i === 0}
                  data-testid={`sidebar-${scope}-up-${it.key}`}
                  title="Move up"
                >
                  <CaretUp size={12} />
                </button>
                <button
                  type="button"
                  className="btn btn-ghost btn-sm"
                  onClick={() => moveDown(i)}
                  disabled={i === list.length - 1}
                  data-testid={`sidebar-${scope}-down-${it.key}`}
                  title="Move down"
                >
                  <CaretDown size={12} />
                </button>
              </div>
            )
          )}
        </div>
      )}
    </div>
  );
}
